/**
 * FloorPlanCertification — PF v2 native configuration UI.
 *
 * Lifts the presentational markup from the legacy `FloorPlanCertificationStepController`
 * (`PurchaseFlow/steps/FloorPlanCertification`) but rewrites ALL data wiring onto the
 * PF v2 store via `useFloorPlanCertConfig` — no constate provider, no imperative
 * handle, no legacy controller/context. F-B1 dies by construction: state lives in
 * `productConfigs` (placed there by a synchronous hydrate `set()` before this mounts),
 * and mounting only READS it.
 *
 * Dropped vs. the legacy controller (all dead in v2 — see floor-plan-certification.spec §5):
 *   - the architect-stamp `MUICheckCard` + `ProductCardHeader` and the
 *     `addFloors`/`removeFloors` extra-certified-floor cart-product side-effects —
 *     these wrote the legacy `usePurchaseFlowProducts` context (NOT the revamp cart);
 *     v2 has no such cart-extras wiring and does not send this DTO to BE. (RISK-A —
 *     has revenue implications; flagged to product/BE.)
 *   - the `{{price}}` `number_of_floors_hint` — no v2 price source; keeping it would
 *     render a misleading "€undefined" extra-charge hint (same class as measurement DF-1).
 *   - nothing on the analytics axis any more: the configuration page announces
 *     this tab for `ENTER_ORDER_LIVING_AREA_CALCULATION_PAGE_SCREEN`, and Save
 *     carries `living_area_calculation_requirement_set_up`. Legacy attached the
 *     whole form to that event; v2 sends the moment only.
 *   - the FE-only sentinel floor — legacy state carried `[sentinel, floor1, …]`; the
 *     native module works with real floors directly (persisted DTO has no sentinel).
 *   - `StepWrapper` step title/subtitle — the v2 configuration page owns the page
 *     title (measurement precedent). Visual-parity item flagged for QA.
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). The two FPC
 * enums are re-declared locally in `floorPlanCertification.dto.ts` (PM DECISION —
 * DF-7); relocating the legacy enums is deferred to the Z cleanup wave.
 *
 * i18n: reuses the legacy `purchase_flow:floor_plan_certification_step.*` namespace
 * verbatim (no re-translation; every key incl. all 21 property types already
 * exists).
 */

import React, { useCallback, useState } from 'react'
import { Box, Fade, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { MUIRadio } from 'components/common/MUIRadio'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { CartMutationHandler } from '../../adapters/bridge'

import { AreaCalculationTypes, FloorPlanCertificationPropertyTypes } from './floorPlanCertification.dto'
import { useFloorPlanCertConfig } from './useFloorPlanCertConfig'

// ─── Component ──────────────────────────────────────────────────────────────

export interface FloorPlanCertificationProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — FPC emits no cart mutations
   *  (dead in v2), so it is ignored. */
  onCartMutation?: CartMutationHandler
}

/**
 * @component FloorPlanCertification
 * Native floor-plan-certification configuration form. Reads/writes `productConfigs`
 * via `useFloorPlanCertConfig`.
 */
export const FloorPlanCertification: React.FC<FloorPlanCertificationProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'floor_plan_certification_step' })
  const { value, patch, setFloorCount } = useFloorPlanCertConfig(cartItemId)

  // Show floor-name errors only after the user has blurred an input (legacy parity).
  const [showError, setShowError] = useState(false)
  const handleInputBlur = useCallback(() => {
    if (!showError) setShowError(true)
  }, [showError])

  // Rebuild the full floors array on a rename — normalizes length to numberOfFloors
  // and reindexes floorNumber, so a sparse/partial persisted DTO stays valid.
  const renameFloor = useCallback((index: number, name: string) => {
    const floors = Array.from({ length: value.numberOfFloors }, (_, i) => ({
      name: i === index ? name : (value.floors[i]?.name ?? ''),
      floorNumber: i + 1,
    }))
    patch({ floors })
  }, [value.numberOfFloors, value.floors, patch])

  return (
    <Stack width="100%" minWidth={0}>
      <Fade in>
          <Stack gap={2}>

            <SystemMessage variant="system" message={t('liability_info')} />

            <SectionedBorderBox
              title={
                <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
                  {t('property_information.property_information_section_title')}
                </Typography>
              }
            >
              <Stack spacing={2} marginBottom={2}>

                {/* Property type */}
                <Box sx={{ maxWidth: '70%' }}>
                  <MUIDropdown
                    button={(isOpen, action) => (
                      <MUIDropdownInput
                        readOnly
                        label={t('property_type')}
                        type={DropdownInputTypes.SELECTOR}
                        value={t(`certification_property_types.${value.propertyType}`)}
                        onClick={action}
                      />
                    )}
                  >
                    <Box height="30rem">
                      {Object.values(FloorPlanCertificationPropertyTypes).map((propertyTypeItem) => (
                        <MUIDropdownItem
                          key={propertyTypeItem}
                          onClick={() => patch({ propertyType: propertyTypeItem })}
                        >
                          <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                            {t(`certification_property_types.${propertyTypeItem}`)}
                          </Typography>
                        </MUIDropdownItem>
                      ))}
                    </Box>
                  </MUIDropdown>
                </Box>

                {/* Number of floors */}
                <MUIInputFieldCounter
                  required
                  isError={value.numberOfFloors === 0}
                  counterValue={value.numberOfFloors}
                  counterOnChange={setFloorCount}
                  inputBoxWidth="14rem"
                  label={t('property_information.number_of_floors')}
                  onBlur={handleInputBlur}
                />

                {/* Per-floor name cards */}
                {value.numberOfFloors > 0 &&
                  <Stack direction="row" flexWrap="wrap" gap={2} width="100%">
                    {Array.from({ length: value.numberOfFloors }).map((_, index) => {
                      const floorNumber = index + 1
                      return (
                        <SectionedBorderBox
                          key={floorNumber}
                          title={
                            <Typography color={GRAY_900} variant="text-lg" fontWeight={600}>
                              {t('property_information.floor', { floorNumber })}
                            </Typography>
                          }
                          sx={{ width: value.numberOfFloors === 1 ? '50%' : 'calc(50% - 1rem)' }}
                        >
                          <MUIInputField
                            required
                            isError={showError && !value.floors[index]?.name}
                            errorText={t('property_information.floor_name_error')}
                            placeholder={t('property_information.floor_name_example')}
                            label={t('property_information.floor_name')}
                            value={value.floors[index]?.name ?? ''}
                            onChange={(e) => renameFloor(index, e.target.value)}
                            onBlur={handleInputBlur}
                          />
                        </SectionedBorderBox>
                      )
                    })}
                  </Stack>
                }

                {/* Area calculation */}
                <Stack gap=".8rem">

                  <Label text={t('area_calculations.field_title')} />

                  {Object.values(AreaCalculationTypes).map((calc) => (
                    <MUIRadio
                      key={calc}
                      name="regulated-living-area-calculation"
                      value={calc}
                      checked={value.regulatedLivingAreaCalculation === calc}
                      onChange={() => patch({ regulatedLivingAreaCalculation: calc })}
                      label={<Label color={GRAY_900} text={t(`area_calculations.${calc}`)} />}
                    />
                  ))}

                </Stack>

              </Stack>
            </SectionedBorderBox>

            {/* Comment */}
            <SectionedBorderBox title={t('property_information.comment')}>
              <MUIInputField
                hintText={t('property_information.comment_hint')}
                isMultiline
                value={value.comment ?? ''}
                onChange={(e) => patch({ comment: e.target.value })}
              />
            </SectionedBorderBox>

        </Stack>
      </Fade>
    </Stack>
  )
}
